import { useEffect, useState } from 'react';

// Claude agent state helpers shared by the card and the ticket panel.
// States come from cmr-backend app/modules/agent/agent_db.py.

export const AGENT_AUTHOR_ID = 'claude-agent';

// The runner owns the ticket right now.
export const RUNNING_STATES = new Set(['queued', 'understanding', 'coding', 'testing']);

// Running states that must keep a heartbeat (queued waits for a runner slot).
const HEARTBEAT_STATES = new Set(['understanding', 'coding', 'testing']);
const HEARTBEAT_TIMEOUT_MS = 2 * 60 * 1000;

export const AGENT_BADGE_STYLES = {
  queued: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  understanding: 'bg-violet-50 text-violet-700 border-violet-200',
  waiting_answer: 'bg-red-50 text-red-700 border-red-200',
  waiting_go: 'bg-orange-50 text-orange-700 border-orange-200',
  coding: 'bg-violet-50 text-violet-700 border-violet-200',
  testing: 'bg-sky-50 text-sky-700 border-sky-200',
  in_review: 'bg-amber-50 text-amber-700 border-amber-200',
  stopped: 'bg-zinc-100 text-zinc-500 border-zinc-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

// Mirrors can_use_ai in cmr-backend app/modules/agent/agent_settings.py: the
// owner always, other members only if the owner ticked them in Settings → Agent.
export function canUseAi(project, userId) {
  if (!project || !userId) return false;
  if (project.owner_id === userId) return true;
  return (project.ai_user_ids || []).includes(userId) && (project.member_ids || []).includes(userId);
}

export function isAgentRunning(agent) {
  return !!agent && RUNNING_STATES.has(agent.state);
}

export function isAgentOffline(agent, now = Date.now()) {
  if (!agent || !HEARTBEAT_STATES.has(agent.state)) return false;
  if (!agent.heartbeat_at) return true;
  return now - new Date(agent.heartbeat_at).getTime() > HEARTBEAT_TIMEOUT_MS;
}

// Re-render every ``ms`` while ``active`` so "no signal" shows up without a new event.
export function useAgentClock(active, ms = 30000) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), ms);
    return () => clearInterval(id);
  }, [active, ms]);
}
